import styled, { css } from 'styled-components';

import { Text } from '../../../../components';
import type { ListPlacement } from '../../game-list-geometry';
import { matchScrollMargin } from '../GameListFilter/GameListFilter.styles';
import { ANIM, COLORS, GAME_LIST_FILTER, SORTABLE_ANIM } from '@/nova/theme/tokens';

/** The list and the FTUX's mark over its first games, placed against it. */
export const RowsFrame = styled.div`
  position: relative;
`;

/**
 * FTUX - Getting Started's games piece lights the list's first games, what players meet first: a box over them that
 * paints nothing and takes no press, with a row's corners (the dim's cut-out follows them).
 */
export const FirstGamesMark = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 12px;
  pointer-events: none;
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/*
 * One game in the order, as slim as a thumbnail allows. Every row is the same height: the drag maths turns a
 * pointer distance into a number of rows. The row's transform comes from the drag (an inline style, as it
 * changes on every pointer move); an idle row carries none, so it forms no stacking context and its open
 * menu can overlap the rows below it.
 */
export const Row = styled.li<{
  $dragged: boolean;
  $animated: boolean;
  $dimmed: boolean;
  $current: boolean;
  $placement: ListPlacement;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  height: 52px;
  padding: 0 2px 0 0;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  /* Press and hold is the drag's gesture: no text selection, no iOS callout, no double-tap zoom delay. */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
  ${({ $placement }) => matchScrollMargin($placement)}
  /* Moving a game must not move the page: without this the browser keeps a moved row that was the scroll
     anchor where it was on screen, scrolling the page after it. */
  overflow-anchor: none;
  transition: ${({ $animated, $dragged }) =>
    $animated
      ? `transform ${$dragged ? SORTABLE_ANIM.SETTLE_MS : SORTABLE_ANIM.SHIFT_MS}ms ${SORTABLE_ANIM.EASE},
         box-shadow ${SORTABLE_ANIM.SETTLE_MS}ms ${SORTABLE_ANIM.EASE},
         opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE}`
      : `opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE}`};

  /* Filtered out by the live filter: faded back, still in its place in the order. */
  opacity: ${({ $dimmed }) => ($dimmed ? GAME_LIST_FILTER.DIMMED_OPACITY : 1)};

  /* The match the filter's steps are on. */
  ${({ $current }) =>
    $current &&
    css`
      border-color: ${COLORS.ctaBorder};
      box-shadow: inset 0 0 0 1px ${COLORS.ctaBorder};
    `}

  /* A row in use (its menu open) is at full strength even when filtered out, or the menu would be faded too. */
  &:focus-within {
    z-index: 3;
    opacity: 1;
  }

  ${({ $dragged }) =>
    $dragged &&
    css`
      z-index: 2;
      box-shadow: ${COLORS.menuShadow};
    `}
`;

export const Handle = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  width: 34px;
  padding: 0;
  border: 0;
  border-radius: 11px 0 0 11px;
  background: transparent;
  color: ${COLORS.textFaint};
  cursor: grab;
  /* Claim the gesture from the page: a drag that starts on the handle never scrolls. */
  touch-action: none;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus-visible {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.text};
  }

  &:active {
    cursor: grabbing;
  }
`;

/* The position the site will render the game at (1-based). */
export const Position = styled(Text).attrs({ tag: 'span' as const })`
  flex: none;
  min-width: 22px;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  text-align: center;
  color: ${COLORS.textMuted};
`;

export const Thumb = styled.div`
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  box-sizing: border-box;
  /* Centres the mark an unavailable game shows in place of its art. */
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.textMuted};
`;

export const Img = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-user-drag: none;
  pointer-events: none;
`;

export const Titles = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 4px;
`;

export const Name = styled(Text).attrs({ tag: 'span' as const })<{ $muted?: boolean }>`
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: ${({ $muted }) => ($muted ? COLORS.textMuted : COLORS.heading)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ID + provider, one line under the name. */
export const Meta = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Kebab = styled.div`
  flex: none;
  display: inline-flex;
`;
